import { useMemo } from 'react';
import $ from 'jquery';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { useObservableEagerState } from 'observable-hooks';
import { isString } from 'lodash-es';
import type { GuideLine as GraphqlGuideLine } from '@/graphql/types';
import warning from './logger';
import i18n from '../translations/i18n';
import { language$ } from './date-fns';
import { schema } from '../schema';

type NamingConventions = Record<string, string>;

export const { defaultGuideline } = schema.namingConventions;

type GuideLine = `${GraphqlGuideLine}`;

export function getNamingConventionsForGuideline(guideline: GuideLine): NamingConventions {
    const translationKey = `NAMING_CONVENTIONS.${guideline}`;

    if (!i18n.exists(translationKey)) {
        warning(`No naming conventions found for guideline: '${guideline}'`);
        return {};
    }

    // Grab the naming conventions from the currently loaded language.
    return i18n.t(translationKey, { returnObjects: true });
}

export const guideline$ = new BehaviorSubject<GuideLine>(defaultGuideline);

export const namingConventions$ = new BehaviorSubject(
    getNamingConventionsForGuideline(defaultGuideline)
);

// Listen for updates on guideline$ and language$.
combineLatest([guideline$, language$])
    .pipe(
        // Map those changes to a naming conventions object.
        map(([guideline]) => getNamingConventionsForGuideline(guideline))
    )
    // Push updates to `namingConventions$`.
    .subscribe(namingConventions$);

export function setNamingConventionsToGuideline(guideLine: GuideLine): void {
    guideline$.next(guideLine);
}

function getNamingConventions(): NamingConventions {
    return namingConventions$.getValue();
}

export function applyNamingConvention(
    text: string,
    namingConventions: NamingConventions = getNamingConventions()
): string {
    return Object.entries(namingConventions).reduce(
        (currentText, [placeholder, guidelineText]) =>
            currentText.replaceAll(`~~${placeholder}~~`, guidelineText),
        text
    );
}

/**
 * @deprecated This is a legacy method as it requires jQuery, migrate to NamingConventionSpan.
 */
export function applyNamingConventionToElement(el: Element): void {
    const $el = $(el);
    const contents = $el.contents();
    const namingConvention = $el.attr('naming-convention');
    if (!namingConvention) {
        throw new Error('Naming convention attribute missing on element.');
    }
    const text = applyNamingConvention(namingConvention);

    // If this element has no content, we can put the text in directly.
    if (contents.length === 0) {
        $el.text(text);
        return;
    }

    // If this element does have content, we have to replace the text per content element.
    contents.each((_i, element) => {
        if (element.nodeType === Node.TEXT_NODE) {
            element.nodeValue = text;
        }
    });
}

function applyNamingConventionsToDOM(): void {
    $('[naming-convention]').each((_index, el) => applyNamingConventionToElement(el));
}

/**
 * @deprecated This is a legacy method as it requires jQuery, migrate to NamingConventionSpan.
 */
export function applyNamingConventionsByGuideline(guideLine: GuideLine): void {
    setNamingConventionsToGuideline(guideLine);
    applyNamingConventionsToDOM();
}

export function useNamingConventions(inputText: string): string;
export function useNamingConventions<T>(inputText: T): T;
export function useNamingConventions<T>(inputText: T | string): T | string {
    const namingConventions = useObservableEagerState(namingConventions$);

    return useMemo(() => {
        if (!isString(inputText)) {
            return inputText;
        }

        return applyNamingConvention(inputText, namingConventions);
    }, [inputText, namingConventions]);
}
